import { createSlice, isAnyOf } from "@reduxjs/toolkit";
import { apiThunk } from "App/store/apiThunk";

/**
 * The supplier's own drivers.
 *
 * All endpoints live under /suppliers/me/drivers and are ownership-checked
 * server-side against a_customer_drivers.supplier_id — the supplier is resolved
 * from the JWT, so one supplier can never read or edit another's driver.
 *
 * KYC saves post the exact payload the web app posts to
 * customer-driver/driver-update; the API runs it through the same driver
 * service, so document statuses behave identically to the web flow.
 */

type DriverArg = { driverId: string };

export const loadMyDrivers = apiThunk<any, void>("drivers/loadMine", {
  server: "login",
  url: "suppliers/me/drivers",
  method: "GET",
  errorMsg: true,
});

export const createMyDriver = apiThunk<any, Record<string, any>>("drivers/create", {
  server: "login",
  url: "suppliers/me/drivers",
  method: "POST",
  successMsg: true,
  errorMsg: true,
});

/** One driver with their full KYC row — the web app's driverProfileDetails. */
export const loadMyDriver = apiThunk<any, DriverArg>("drivers/loadOne", {
  server: "login",
  url: ({ driverId }) => `suppliers/me/drivers/${driverId}`,
  method: "GET",
  errorMsg: true,
});

export const updateMyDriver = apiThunk<any, DriverArg & { data: Record<string, any> }>(
  "drivers/update",
  {
    server: "login",
    url: ({ driverId }) => `suppliers/me/drivers/${driverId}`,
    method: "PUT",
    body: ({ data }) => data,
    successMsg: true,
    errorMsg: true,
  }
);

export const saveDriverKyc = apiThunk<any, DriverArg & { data: Record<string, any> }>(
  "drivers/saveKyc",
  {
    server: "login",
    url: ({ driverId }) => `suppliers/me/drivers/${driverId}/kyc`,
    method: "POST",
    body: ({ data }) => data,
    successMsg: true,
    errorMsg: true,
  }
);

export const submitDriverKyc = apiThunk<any, DriverArg>("drivers/submitKyc", {
  server: "login",
  url: ({ driverId }) => `suppliers/me/drivers/${driverId}/kyc/submit`,
  method: "POST",
  body: () => ({}),
  successMsg: true,
  errorMsg: true,
});

/** Release the driver from the company. Their account and KYC stay intact. */
export const removeMyDriver = apiThunk<any, DriverArg>("drivers/remove", {
  server: "login",
  url: ({ driverId }) => `suppliers/me/drivers/${driverId}`,
  method: "DELETE",
  successMsg: true,
  errorMsg: true,
});

/**
 * Put the driver on one of the supplier's vehicles. A driver already on
 * another of the supplier's vehicles is moved; a vehicle another driver holds
 * must be released first. Returns the updated driver row.
 */
export const assignDriverVehicle = apiThunk<any, DriverArg & { vehicleId: number }>(
  "drivers/assignVehicle",
  {
    server: "login",
    url: ({ driverId }) => `suppliers/me/drivers/${driverId}/vehicle`,
    method: "PUT",
    body: ({ vehicleId }) => ({ vehicle_id: vehicleId }),
    successMsg: true,
    errorMsg: true,
  }
);

/** Take the driver off their vehicle (they go offline). Returns the updated driver row. */
export const releaseDriverVehicle = apiThunk<any, DriverArg>("drivers/releaseVehicle", {
  server: "login",
  url: ({ driverId }) => `suppliers/me/drivers/${driverId}/vehicle`,
  method: "DELETE",
  successMsg: true,
  errorMsg: true,
});

/**
 * Upload a KYC document. Same endpoint the web app uses — it only needs a
 * valid session, and returns `{ file }` with the stored URL.
 */
export const uploadDriverFile = apiThunk<any, FormData>("drivers/uploadFile", {
  server: "login",
  url: "cust/licenseupload",
  method: "POST",
  body: (form) => form as unknown as Record<string, any>,
  successMsg: true,
  errorMsg: true,
});

/** OCR the uploaded document to pull out expiry dates etc., as the web app does. */
export const processDriverImage = apiThunk<any, Record<string, any>>("drivers/processImage", {
  server: "taxi",
  url: "image/process-image",
  method: "POST",
  successMsg: true,
  errorMsg: true,
});

/**
 * The admin's driver view for one owned driver: account fields, the KYC row
 * on `driver`, vehicles, plus `compare` (what changed since the last
 * approval) and parsed `review_messages`.
 */
export const loadDriverProfile = apiThunk<any, DriverArg>("drivers/loadProfile", {
  server: "login",
  url: ({ driverId }) => `suppliers/me/drivers/${driverId}/profile`,
  method: "GET",
  errorMsg: true,
});

/** Personal details (and optionally KYC fields under `driver`), as the admin's driver edit saves them. */
export const saveDriverProfile = apiThunk<any, DriverArg & { data: Record<string, any> }>(
  "drivers/saveProfile",
  {
    server: "login",
    url: ({ driverId }) => `suppliers/me/drivers/${driverId}/profile`,
    method: "PUT",
    body: ({ data }) => data,
    successMsg: true,
    errorMsg: true,
  }
);

export type KycDecision = {
  action: "approve" | "reject";
  reason?: string;
  review_messages?: Record<string, { level: string; msg: string }>;
};

/** Final Accept / Reject of the driver's KYC — the same action the admin takes. */
export const reviewDriverKyc = apiThunk<any, DriverArg & KycDecision>("drivers/reviewKyc", {
  server: "login",
  url: ({ driverId }) => `suppliers/me/drivers/${driverId}/kyc/action`,
  method: "POST",
  body: ({ action, reason, review_messages }) => ({ action, reason, review_messages }),
  successMsg: true,
  errorMsg: true,
});

export interface DriverRow {
  /** The driver's customer id — what every /me/drivers/:driverId route takes. */
  id: string;
  driver_id: number;
  first_name: string;
  last_name: string;
  name: string;
  cust_email: string | null;
  country_code: string;
  cust_mobile: string;
  customer_code: string | null;
  account_status: string;
  login_status: string | null;
  driver_verified: string | null;
  zone_id: number | null;
  status: string;
  can_submit_review: string;
  current_vehicle: {
    id: number;
    vehicle_registration: string;
    vehicle_brand_name: string | null;
    vehicle_model_no: string | null;
  } | null;
  [key: string]: any;
}

interface DriversState {
  list: DriverRow[];
  /** The driver whose KYC screen is open. */
  current: DriverRow | null;
  /** The admin-style profile of the driver being reviewed (loadDriverProfile). */
  profile: any | null;
  errors: any;
  loading: boolean;
  saving: boolean;
  /** Set after a successful create/remove so the list screen can refetch and reset. */
  lastMutation: string | null;
}

const initialState: DriversState = {
  list: [],
  current: null,
  profile: null,
  errors: null,
  loading: false,
  saving: false,
  lastMutation: null,
};

const driversSlice = createSlice({
  name: "drivers",
  initialState,
  reducers: {
    clearDriverMutation: (state) => {
      state.lastMutation = null;
    },
    clearCurrentDriver: (state) => {
      state.current = null;
      state.profile = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadMyDrivers.pending, (state) => {
        state.errors = null;
        state.loading = true;
      })
      .addCase(loadMyDrivers.fulfilled, (state, action) => {
        state.list = action.payload?.data?.drivers ?? [];
        state.loading = false;
      })
      .addCase(loadMyDrivers.rejected, (state, action) => {
        state.errors = action.payload;
        state.loading = false;
      })

      .addCase(createMyDriver.pending, (state) => {
        state.errors = null;
        state.saving = true;
      })
      .addCase(createMyDriver.fulfilled, (state) => {
        state.saving = false;
        state.lastMutation = "added";
      })
      .addCase(createMyDriver.rejected, (state, action) => {
        state.errors = action.payload;
        state.saving = false;
      })

      .addCase(loadMyDriver.fulfilled, (state, action) => {
        state.current = action.payload?.data ?? null;
      })
      .addCase(loadMyDriver.rejected, (state, action) => {
        state.errors = action.payload;
      })

      .addCase(updateMyDriver.pending, (state) => {
        state.saving = true;
      })
      .addCase(updateMyDriver.fulfilled, (state, action) => {
        state.saving = false;
        if (action.payload?.data) state.current = action.payload.data;
      })
      .addCase(updateMyDriver.rejected, (state, action) => {
        state.saving = false;
        state.errors = action.payload;
      })

      .addCase(submitDriverKyc.fulfilled, (state, action) => {
        if (action.payload?.data) state.current = action.payload.data;
      })

      .addCase(loadDriverProfile.fulfilled, (state, action) => {
        state.profile = action.payload?.data ?? null;
      })
      .addCase(saveDriverProfile.pending, (state) => {
        state.saving = true;
      })
      .addCase(saveDriverProfile.fulfilled, (state, action) => {
        state.saving = false;
        if (action.payload?.data) state.profile = action.payload.data;
      })
      .addCase(saveDriverProfile.rejected, (state, action) => {
        state.saving = false;
        state.errors = action.payload;
      })
      .addCase(reviewDriverKyc.pending, (state) => {
        state.saving = true;
      })
      .addCase(reviewDriverKyc.fulfilled, (state, action) => {
        state.saving = false;
        if (action.payload?.data) state.profile = action.payload.data;
      })
      .addCase(reviewDriverKyc.rejected, (state, action) => {
        state.saving = false;
        state.errors = action.payload;
      })

      .addCase(removeMyDriver.pending, (state) => {
        state.saving = true;
      })
      .addCase(removeMyDriver.fulfilled, (state, action) => {
        state.saving = false;
        state.lastMutation = "removed";
        const id = action.meta.arg?.driverId;
        state.list = state.list.filter((d) => d.id !== id);
      })
      .addCase(removeMyDriver.rejected, (state, action) => {
        state.saving = false;
        state.errors = action.payload;
      })
      .addMatcher(
        isAnyOf(assignDriverVehicle.pending, releaseDriverVehicle.pending),
        (state) => {
          state.saving = true;
        }
      )
      .addMatcher(
        isAnyOf(assignDriverVehicle.fulfilled, releaseDriverVehicle.fulfilled),
        (state, action) => {
          state.saving = false;
          const row = action.payload?.data;
          if (!row) return;
          state.list = state.list.map((d) => (d.id === row.id ? row : d));
          if (state.current?.id === row.id) state.current = row;
        }
      )
      .addMatcher(
        isAnyOf(assignDriverVehicle.rejected, releaseDriverVehicle.rejected),
        (state, action) => {
          state.saving = false;
          state.errors = action.payload;
        }
      );
  },
});

export const { clearDriverMutation, clearCurrentDriver } = driversSlice.actions;
export default driversSlice.reducer;
